import React, { useState, useEffect } from 'react';
import { formatNumber, formatDate, formatPercentage, calculatePercentage } from '../../../utils/formatters';
import './InvestmentCard.css';

const InvestmentCard = ({ investment, index, timeUnit = 300 }) => {
  const {
    amount,
    timestamp,
    roiEarned,
    totalEarned,
    cap300,
    remaining,
    active,
    invType,
    lastROITimestamp
  } = investment;

  const [nextROITimer, setNextROITimer] = useState('');

  useEffect(() => {
    if (!active || !lastROITimestamp) return;

    const updateTimer = () => {
      const now = Math.floor(Date.now() / 1000); // Current time in seconds
      const lastROI = parseInt(lastROITimestamp);
      
      // Calculate how many cycles have passed since lastROITimestamp
      const timeSinceLastROI = now - lastROI;
      const cyclesPassed = Math.floor(timeSinceLastROI / timeUnit);
      
      // Calculate when the next ROI will be available
      const nextROITime = lastROI + ((cyclesPassed + 1) * timeUnit);
      const diff = nextROITime - now;

      if (diff <= 0) {
        // Should restart - calculate next cycle
        setNextROITimer('ROI Ready!');
        return;
      }

      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;

      if (hours > 0) {
        setNextROITimer(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setNextROITimer(`${minutes}m ${seconds}s`);
      } else {
        setNextROITimer(`${seconds}s`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [lastROITimestamp, active, timeUnit]);

  const progress = calculatePercentage(parseFloat(totalEarned), parseFloat(cap300));
  const invTypes = ['Investment', 'Reinvest'];

  return (
    <div className={`investment-card ${active ? 'active' : 'capped'}`}>
      <div className="investment-card-header">
        <div className="investment-card-index">#{index + 1}</div>
        <div className="investment-card-type">{invTypes[invType] || 'Investment'}</div>
        <div className={`investment-card-status ${active ? 'status-active' : 'status-capped'}`}>
          {active ? 'Active' : 'Capped'}
        </div>
      </div>

      <div className="investment-card-amount">
        <span className="amount-value">{formatNumber(amount)}</span>
        <span className="amount-unit">USDT</span>
      </div>

      {active && (
        <div className="investment-roi-timer">
          <span className="roi-timer-label">⏰ Next ROI:</span>
          <span className={`roi-timer-value ${nextROITimer === 'ROI Ready!' ? 'ready' : ''}`}>
            {nextROITimer || 'Calculating...'}
          </span>
        </div>
      )}

      <div className="investment-card-progress">
        <div className="progress-header">
          <span>Progress</span>
          <span>{formatPercentage(progress)} / 300%</span>
        </div>
        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{ width: `${Math.min(progress / 3, 100)}%` }}
          />
        </div>
      </div>

      <div className="investment-card-stats-row">
        <div className="stat-cell">
          <span className="stat-value text-cyan">{formatNumber(roiEarned, 2)}</span>
          <span className="stat-label">ROI</span>
        </div>
        <div className="stat-cell">
          <span className="stat-value text-purple">{formatNumber(totalEarned, 2)}</span>
          <span className="stat-label">Earned</span>
        </div>
        <div className="stat-cell">
          <span className="stat-value text-success">{formatNumber(remaining, 2)}</span>
          <span className="stat-label">Left</span>
        </div>
        <div className="stat-cell">
          <span className="stat-value">{formatNumber(cap300, 0)}</span>
          <span className="stat-label">Cap</span>
        </div>
      </div>

      <div className="investment-card-footer">
        <span className="investment-date">Started: {formatDate(timestamp)}</span>
      </div>
    </div>
  );
};

export default InvestmentCard;
